/* eslint-disable import/no-named-as-default */
import React from 'react';
import classNames from 'classnames';

import dispatchNewEvent from '../../utils/analytics/dispatchEvent';
import postMonetateImpression from '../../utils/monetate/postMonetateImpression';
import { TopLinks } from '../link-list/LinkList';
import { getTopLinks, getTopLinksWithMenu } from '../links';
import LogoBP from '../logo/LogoBP';
import Logo from '../logo/Logo';
import UserTools from '../user-tools/UserTools';
import Search from '../search/Search';
import TypeaheadOverlay from '../typeahead-overlay/TypeaheadOverlay';
import {
  desktopMediaQuery,
  isDesktop,
} from '../../utils/device/identifyDevice';
import isMonetateGroup from '../../utils/monetate/isMonetateGroup';
import EnvVarsFeaturesContext from '../../scaffold/EnvVarsFeaturesContext';

import styles from './responsive-header.scss';
import BrandPromiseOverlay from '../typeahead-overlay/BrandPromiseOverlay';
import BrandPromiseCloseButton from '../typeahead-overlay/BrandPromiseCloseButton';
import DeliveryThreshold from '../delivery-threshold/DeliveryThreshold';

const logoMaxHeight = 70;
const minHeightOfLogoTextLegible = 45;
const brandmarkHeight = 36;
const peepHideThreshold = 150;
const peepShowThreshold = 100;

export class ResponsiveHeader extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      showNav: false,
      swapLogo: false,
      previousScrollPosition: 0,
      wasScrollingUp: false,
      previousDirectionChange: 0,
      isVisible: true,
      isDesktop: false,
      isFocused: false,
    };
    const { features, envVars } = this.context;
    this.peepHeader = features.peepHeader;
    this.monetateExperiences = features.monetateExperiences;
    this.isBrandPromiseActive = features.isBrandPromiseActive;
    this.isNewPreNavActive = isMonetateGroup(features, 'prenav-redesign');
    this.monetate = envVars.monetate;
    this.slimMobileHeader = isMonetateGroup(features, 'slim-mobile-header');
    this.handleDeviceOrientation = this.handleDeviceOrientation.bind(this);
    this.updateView = this.updateView.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.notifyMonetate = this.notifyMonetate.bind(this);
    this.searchInputFocus = this.searchInputFocus.bind(this);
    this.escKeyHandler = this.escKeyHandler.bind(this);
    this.searchInputBlur = this.searchInputBlur.bind(this);
    this.handleUnpeep = this.handleUnpeep.bind(this);
    this.onlyPeepAtTop = features.onlyPeepAtTop || false;
    this.scrollbarWidth = 0;
    this.header = React.createRef();
    this.headerContainer = React.createRef();
    this.recordImpression = isMonetateGroup(features, 'record-impression');
    this.isDeliveryThresholdActive = isMonetateGroup(
      features,
      'delivery-threshold'
    );
    if (this.isNewPreNavActive) {
      this.topLinksData = getTopLinksWithMenu();
    } else {
      this.topLinksData = getTopLinks(features);
    }
  }

  componentDidMount() {
    if (this.props.isBrandPromiseActive) {
      this.isBrandPromiseActive = this.props.isBrandPromiseActive;
    }
    this.scrollbarWidth = window.innerWidth - document.body.clientWidth;
    this.updateView();
    this.updateHeight();
    window.addEventListener('scroll', this.updateView);
    window.addEventListener('resize', this.updateHeight);
    window.addEventListener('keydown', this.escKeyHandler);

    this.handleDeviceOrientation();
    window.addEventListener(
      'jl-events.browser.mobileHeaderMenuClick',
      this.notifyMonetate,
      { once: true }
    );
    window.addEventListener(
      'jl-events.browser.mobileHeaderCloseMenu',
      this.onMenuButtonClick
    );
    window.addEventListener(
      'jl-events.browser.mobileHeaderUnpeep',
      this.handleUnpeep
    );
    desktopMediaQuery().addListener(this.handleDeviceOrientation);

    if (this.recordImpression) {
      postMonetateImpression(
        this.monetate,
        'record-impression',
        this.monetateExperiences
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.updateView);
    window.removeEventListener('resize', this.updateHeight);
    window.removeEventListener('keydown', this.escKeyHandler);
    window.removeEventListener(
      'jl-events.browser.mobileHeaderMenuClick',
      this.notifyMonetate,
      { once: true }
    );
    window.removeEventListener(
      'jl-events.browser.mobileHeaderCloseMenu',
      this.onMenuButtonClick
    );
    window.removeEventListener(
      'jl-events.browser.mobileHeaderUnpeep',
      this.handleUnpeep
    );
  }

  onMenuButtonClick(e) {
    const showNav = !this.state.showNav;
    e.preventDefault();

    this.setState({ showNav }, this.updateLogo);
    this.props.updateMobileNavState(showNav);

    const menuClick = new CustomEvent(
      'jl-events.browser.mobileHeaderMenuClick',
      {
        detail: {
          hide: showNav,
        },
      }
    );

    window.dispatchEvent(menuClick);

    if (showNav) {
      window.document.body.classList.add('lockBody');
    } else {
      window.document.body.classList.remove('lockBody');
    }

    this.triggerMenuButtonAnalytics();
  }

  setVisibility() {
    const {
      previousScrollPosition,
      previousDirectionChange,
      wasScrollingUp,
      isVisible,
      showNav,
    } = this.state;

    if (isDesktop()) {
      this.setState({ isVisible: true });
      return;
    }

    const currentScrollPosition = window.pageYOffset;
    const isCurrentlyScrollingUp =
      currentScrollPosition < previousScrollPosition;
    const newVisibilityState = {};

    const scrollDirectionDidChange = () =>
      wasScrollingUp !== isCurrentlyScrollingUp;

    const scrollPositionOffTopOfPage = () => currentScrollPosition < 0;

    const headerAtTopOfScreen = () => currentScrollPosition < peepHideThreshold;

    const updatePreviousDirectionChange = () => {
      if (scrollDirectionDidChange()) {
        return scrollPositionOffTopOfPage() || showNav
          ? previousDirectionChange
          : currentScrollPosition;
      }
      return previousDirectionChange;
    };

    const scrolledUpPastPeepThreshold = () => {
      const showThreshold = previousDirectionChange - peepShowThreshold;

      let showOnScrollUp = isCurrentlyScrollingUp;
      if (this.onlyPeepAtTop) {
        showOnScrollUp = false;
      }

      return (
        !isVisible && showOnScrollUp && currentScrollPosition < showThreshold
      );
    };

    const scrolledDownPastPeepThreshold = () => {
      const hideThreshold = previousDirectionChange + peepHideThreshold;

      return (
        isVisible &&
        !isCurrentlyScrollingUp &&
        currentScrollPosition > hideThreshold
      );
    };

    const showHeader = () => {
      newVisibilityState.isVisible = true;
      this.dispatchPeepEvent(newVisibilityState.isVisible);
    };

    const hideHeader = () => {
      newVisibilityState.isVisible = false;
      this.dispatchPeepEvent(newVisibilityState.isVisible);
    };

    if (scrolledUpPastPeepThreshold() || headerAtTopOfScreen()) {
      showHeader();
    }

    if (scrolledDownPastPeepThreshold()) {
      hideHeader();
    }

    this.setState({
      ...newVisibilityState,
      previousDirectionChange: updatePreviousDirectionChange(),
      wasScrollingUp: isCurrentlyScrollingUp,
      previousScrollPosition: scrollPositionOffTopOfPage()
        ? 0
        : currentScrollPosition,
    });
  }

  triggerMenuButtonAnalytics() {
    dispatchNewEvent('dataStateChange', {
      action: 'topNav - Side Flyout',
      type: this.state.showNav ? 'Closed' : 'Open',
    });
  }

  // eslint-disable-next-line class-methods-use-this
  dispatchPeepEvent(peeped) {
    const event = new CustomEvent('jl-events.browser.mobileHeaderPeepUpdated', {
      detail: { peeped },
    });

    window.dispatchEvent(event);
  }

  notifyMonetate() {
    postMonetateImpression(
      this.monetate,
      'burger-menu-opened',
      this.monetateExperiences
    );
    postMonetateImpression(
      this.monetate,
      'remove-dont-miss',
      this.monetateExperiences
    );
  }

  handleUnpeep() {
    if (!isDesktop() && window.pageYOffset >= peepHideThreshold) {
      this.dispatchPeepEvent(false);
      this.setState({ isVisible: false });
    }
  }

  handleDeviceOrientation() {
    const showNav = false;
    let newState = { isDesktop: false };

    if (isDesktop()) {
      newState = { showNav, swapLogo: false, isDesktop: true };
      this.props.updateMobileNavState(showNav);
      window.document.body.classList.remove('lockBody');
    }
    this.setState({ ...newState });
  }

  updateLogo() {
    if (isDesktop()) {
      this.setState({ swapLogo: false });
      return;
    }
    const maxHeight = this.slimMobileHeader ? brandmarkHeight : logoMaxHeight;
    const heightTextLegible = this.slimMobileHeader
      ? brandmarkHeight
      : minHeightOfLogoTextLegible;

    let logoHeight = maxHeight - window.pageYOffset;
    logoHeight = logoHeight > maxHeight ? maxHeight : logoHeight;

    const { showNav } = this.state;

    if (showNav || logoHeight <= brandmarkHeight) {
      this.setState({ swapLogo: true });
    } else {
      if (logoHeight < heightTextLegible) {
        this.setState({ swapLogo: true });
      }
      if (logoHeight > heightTextLegible) {
        this.setState({ swapLogo: false });
      }
    }
  }

  handleOverlayKeyPress(event) {
    if (event.key === 'Enter') {
      this.onMenuButtonClick(event);
    }
  }

  updateView() {
    if (!this.state.isFocused) {
      requestAnimationFrame(() => {
        this.updateLogo();
        this.setVisibility();
      });
    }
  }

  updateHeight() {
    if (!this.isBrandPromiseActive) {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      if (!this.state.isFocused) {
        requestAnimationFrame(() => {
          if (this?.headerContainer?.current && this?.header?.current) {
            this.headerContainer.current.style.height = this.header.current.scrollHeight;
          }
        });
      }
    }
  }

  searchInputFocus() {
    window.scrollTo(0, 0);
    this.setState({ isFocused: true });
    document.body.classList.add(styles.typeaheadOpen);
    document.body.style.paddingRight = `${this.scrollbarWidth}px`;
    postMonetateImpression(
      this.monetate,
      'typeahead-content',
      this.monetateExperiences
    );
    postMonetateImpression(
      this.monetate,
      'typeahead-recs',
      this.monetateExperiences
    );
  }

  escKeyHandler(event) {
    if (event.keyCode === 27) {
      document.activeElement.blur();
      this.searchInputBlur();
    }
  }

  searchInputBlur() {
    document.body.style.paddingRight = '';
    this.setState({ isFocused: false });
    document.body.classList.remove(styles.typeaheadOpen);
  }

  render() {
    const { showNav, swapLogo, isVisible } = this.state;

    const wrapperClasses = classNames(styles.header, {
      [styles.slimHeader]: this.slimMobileHeader,
      [styles.navOpenPersistent]: showNav,
      [styles.header__bp]: this.isBrandPromiseActive,
      [styles.header__threshold]:
        this.isDeliveryThresholdActive && !this.state.isFocused,
    });

    const overlayClasses = classNames(styles.overlay, {
      [styles.showOverlay]: showNav,
    });

    const splashClasses = classNames(
      this.state.isFocused ? styles.expandedSearch : styles.search,
      {
        [styles.search__bp]: this.isBrandPromiseActive,
        [styles.expandedSearch__bp]:
          this.isBrandPromiseActive && this.state.isFocused,
      }
    );

    const containerClasses = classNames(styles.container, {
      [styles.peepHeader]: this.peepHeader,
      [styles.peep]: this.peepHeader && !isVisible && !showNav,
      [styles.container__bp]: this.isBrandPromiseActive,
      [styles.container__bp__active]:
        this.isBrandPromiseActive && this.state.isFocused,
    });

    const typeaheadSeparatorClasses = classNames(styles.typeaheadSeparator, {
      [styles.typeaheadSeparator__hidden]: !this.state.isFocused,
    });

    return (
      <div
        className={wrapperClasses}
        ref={this.headerContainer}
        data-testid="header-wrapper"
      >
        <div
          className={containerClasses}
          ref={this.header}
          data-testid="header-container"
        >
          {this.isDeliveryThresholdActive && (
            <DeliveryThreshold hidden={this.state.isFocused} />
          )}
          <div
            className={classNames(styles.topBar, {
              [styles.topBar__bp]: this.isBrandPromiseActive,
              [styles.topBar__threshold]:
                this.isDeliveryThresholdActive && !this.state.isFocused,
              [styles.topBar__prenav]: this.isNewPreNavActive,
              [styles.topBar__bp__active]:
                this.isBrandPromiseActive && this.state.isFocused,
            })}
          >
            <div
              className={classNames(styles.topLinks, {
                [styles.topLinks__bp]: this.isBrandPromiseActive,
                [styles.topLinks__threshold]: this.isDeliveryThresholdActive,
                [styles.topLinks__prenav]: this.isNewPreNavActive,
              })}
              data-testid="header-top-links"
            >
              <TopLinks
                links={this.topLinksData}
                isBrandPromiseActive={this.isBrandPromiseActive}
                isThresholdActive={this.isDeliveryThresholdActive}
                isNewPreNavActive={this.isNewPreNavActive}
              />
            </div>
          </div>
          {this.isBrandPromiseActive ? <div className={styles.greenBar} /> : ''}

          <div
            className={classNames(styles.logoRow, {
              [styles.logoRow__bp]: this.isBrandPromiseActive,
              [styles.logoRow__bp__active]:
                this.isBrandPromiseActive && this.state.isFocused,
            })}
          >
            {this.isBrandPromiseActive ? (
              <LogoBP splashScreen={this.state.isFocused} />
            ) : (
              <Logo
                showMark={
                  swapLogo || (!this.state.isDesktop && this.slimMobileHeader)
                }
                splashScreen={this.state.isFocused}
                slimMobileHeader={this.slimMobileHeader}
              />
            )}
            <div
              className={splashClasses}
              id="mobile-search"
              data-testid="mobile-search"
            >
              <Search
                hideSearch={showNav || (this.peepHeader && !isVisible)}
                id="mobileSearch"
                onFocus={this.searchInputFocus}
                isTypeaheadOpen={this.state.isFocused}
                isBrandPromiseActive={this.isBrandPromiseActive}
              />
            </div>
            {this.isBrandPromiseActive ? (
              <BrandPromiseCloseButton
                isTypeaheadOpen={this.state.isFocused}
                onCloseButtonClick={this.searchInputBlur}
              />
            ) : (
              <TypeaheadOverlay
                isTypeaheadOpen={this.state.isFocused}
                onCloseButtonClick={this.searchInputBlur}
              />
            )}

            <UserTools
              isMenuOpen={showNav}
              isBrandPromiseActive={this.isBrandPromiseActive}
              onMenuButtonClick={e => this.onMenuButtonClick(e)}
              isTypeaheadOpen={this.state.isFocused}
            />
          </div>
          {this.isBrandPromiseActive && (
            <BrandPromiseOverlay
              isTypeaheadOpen={this.state.isFocused}
              onCloseButtonClick={this.searchInputBlur}
            />
          )}
          <hr
            data-testid="typeahead-separator"
            className={typeaheadSeparatorClasses}
          />
        </div>
        {showNav && (
          <div
            className={overlayClasses}
            onClick={e => this.onMenuButtonClick(e)}
            role="button"
            tabIndex={0}
            onKeyPress={this.handleOverlayKeyPress}
          />
        )}
      </div>
    );
  }
}

ResponsiveHeader.contextType = EnvVarsFeaturesContext;

export default ResponsiveHeader;
